// ink-component
import React, { ReactNode, useContext } from 'react';
import SideNavContentContainer, {
  SideNavContentContainerProps,
} from './components/SideNavContentContainer/SideNavContentContainer';
import SideNavFilter, { SideNavFilterProps } from './components/SideNavFilter/SideNavFilter';
import SideNavGroup, { SideNavGroupProps } from './components/SideNavGroup/SideNavGroup';
import SideNavGroupItem, { SideNavGroupItemProps } from './components/SideNavGroupItem/SideNavGroupItem';
import SideNavGroupItems, { SideNavGroupItemsProps } from './components/SideNavGroupItems/SideNavGroupItems';
import SideNavGroups, { SideNavGroupsProps } from './components/SideNavGroups/SideNavGroups';
import SideNavGroupToggle, { SideNavGroupToggleProps } from './components/SideNavGroupToggle/SideNavGroupToggle';
import SideNavItem, { SideNavItemProps } from './components/SideNavItem/SideNavItem';
import SideNavSectionLabel, { SideNavSectionLabelProps } from './components/SideNavSectionLabel/SideNavSectionLabel';
import SideNavStickyContainer, {
  SideNavStickyContainerProps,
} from './components/SideNavStickyContainer/SideNavStickyContainer';
import SideNavToggle, { SideNavToggleProps } from './components/SideNavToggle/SideNavToggle';
import { SideNavContext } from './SideNavContext';
import getGlobalProps from '../../helpers/constants/global-props';
import { StyledSideNav, StyledSideNavWrapper } from './styles';

interface SideNavProps extends React.HTMLAttributes<HTMLElement> {
  /** Child content */
  children: ReactNode;
  /** @ignore */
  hideFilter?: boolean;
  /** @ignore */
  width?: string;
  /** Marks the element with a given ID to make it easier to select it on tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Wrapper for vertical side navigation elements
 * @group SideNav
 * @categories navigation
 * @aka nav
 * @HTMLType HTMLNavElement
 */
const SideNav = (props: SideNavProps) => {
  const { hideFilter, width, children, 'data-testid': dataTestId, ...rest } = props;
  const { isClosed, isCompact, isMobile } = useContext(SideNavContext);

  return (
    <StyledSideNavWrapper isMobile={isMobile} isCompact={isCompact}>
      <StyledSideNav
        {...rest}
        $width={width}
        isClosed={isClosed}
        isCompact={isCompact}
        isMobile={isMobile}
        hideFilter={hideFilter}
        data-testid={dataTestId}
        children={children}
        {...globalProps}
      />
    </StyledSideNavWrapper>
  );
};

SideNav.displayName = 'SideNav';

SideNav.ContentContainer = SideNavContentContainer;
SideNav.Filter = SideNavFilter;
SideNav.Group = SideNavGroup;
SideNav.GroupItem = SideNavGroupItem;
SideNav.GroupItems = SideNavGroupItems;
SideNav.Groups = SideNavGroups;
SideNav.GroupToggle = SideNavGroupToggle;
SideNav.Item = SideNavItem;
SideNav.SectionLabel = SideNavSectionLabel;
SideNav.StickyContainer = SideNavStickyContainer;
SideNav.Toggle = SideNavToggle;
SideNav.Context = SideNavContext;

export {
  SideNavProps,
  SideNavContentContainerProps,
  SideNavFilterProps,
  SideNavGroupProps,
  SideNavGroupItemProps,
  SideNavGroupItemsProps,
  SideNavGroupsProps,
  SideNavGroupToggleProps,
  SideNavItemProps,
  SideNavSectionLabelProps,
  SideNavStickyContainerProps,
  SideNavToggleProps,
};

export default SideNav;
